import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

interface UserDropdownProps {
  scrolled: boolean;
}

export default function UserDropdown({ scrolled }: UserDropdownProps) {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) return null;

  const initials = user.full_name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  const menuItems = [
    {
      icon: "ri-user-line",
      label: t("account.my_account"),
      path: "/account",
    },
    {
      icon: "ri-shopping-bag-line",
      label: t("account.orders"),
      path: "/account/orders",
    },
    {
      icon: "ri-heart-line",
      label: t("account.wishlist"),
      path: "/account/wishlist",
    },
    {
      icon: "ri-settings-3-line",
      label: t("account.settings"),
      path: "/account/settings",
    },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 p-1.5 rounded-full transition-colors cursor-pointer ${
          scrolled
            ? "hover:bg-background-200/70"
            : "hover:bg-white/10"
        }`}
        aria-label="User menu"
      >
        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.full_name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            initials
          )}
        </div>
        <span
          className={`hidden lg:block text-sm font-medium whitespace-nowrap ${
            scrolled ? "text-foreground-700" : "text-white/90"
          }`}
        >
          {user.full_name.split(" ")[0]}
        </span>
        {open ? (
          <i className={`hidden lg:block text-xs ${scrolled ? "text-foreground-400" : "text-white/60"} ri-arrow-up-s-line`}></i>
        ) : (
          <i className={`hidden lg:block text-xs ${scrolled ? "text-foreground-400" : "text-white/60"} ri-arrow-down-s-line`}></i>
        )}
      </button>

      {open && (
        <div
          className={`absolute top-full mt-2 ${
            isAr ? "left-0" : "right-0"
          } w-64 bg-background-50 border border-background-200 rounded-xl z-50 overflow-hidden`}
        >
          {/* User info */}
          <div className="px-4 py-3 border-b border-background-200">
            <p className="text-sm font-semibold text-foreground-900">
              {user.full_name}
            </p>
            <p className="text-xs text-foreground-500 mt-0.5">
              {user.country_code} {user.phone.slice(0, 3)}***{user.phone.slice(-3)}
            </p>
          </div>

          {/* Menu items */}
          <div className="py-2">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className={`${item.icon} text-base text-foreground-400 w-5 text-center`}></i>
                {item.label}
              </Link>
            ))}
          </div>

          {/* Logout */}
          <div className="border-t border-background-200 py-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-r-line text-base w-5 text-center"></i>
              {t("nav.logout")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}